import WebServiceRequest from '../Api/WebServiceRequest'

// ============================================================
//  ProductMedia Endpoints (based on OpenAPI spec)
// ============================================================

// 1. Upload a file and create Media for a product (admin only)
class ProductMediaUploadRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }
    setParams(data) {
        // data = { productId, file, mediaType, title, priority, isMain, isReachable, isLocked }
        // file is a FormData with fields: ContentType, ContentDisposition, Headers, Length, Name, FileName
        // We'll expect the caller to pass FormData that includes the file and other fields.
        super.setTag(`v1/product-media/upload/${data.productId}`)
        super.setFormData(data.formData) // expects FormData
    }
}

// 2. Delete a Media (admin only)
class ProductMediaDeleteRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('DELETE')
    }
    setParams(data) {
        // data = { mediaId }
        super.setTag(`v1/product-media/${data.mediaId}`)
        super.setRequestParam(data)
    }
}

// 3. Update Media information (admin only)
class ProductMediaUpdateRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('PUT')
    }
    setParams(data) {
        // data = { mediaId, title, priority, isReachable, isLocked }
        super.setTag(`v1/product-media/${data.mediaId}`)
        super.setRequestParamDataObj(data)
    }
}

// 4. Toggle lock status of a Media (admin only)
class ProductMediaToggleLockRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }
    setParams(data) {
        // data = { mediaId }
        super.setTag(`v1/product-media/${data.mediaId}/toggle-lock`)
        super.setRequestParam(data)
    }
}

// 5. Change priority (order) of a Media (admin only)
class ProductMediaChangePriorityRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }
    setParams(data) {
        // data = { mediaId, newPriority: number }
        super.setTag(`v1/product-media/${data.mediaId}/change-priority`)
        // The request body is just an integer (new priority)
        super.setRequestParamDataObj(data.newPriority)
    }
}

// 6. Get list of Media for a product (with role-based access)
class ProductMediaGetByProductRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }
    setParams(data) {
        // data = { productId, mediaType? }
        super.setTag(`v1/product-media/product/${data.productId}`)
        const query = {}
        if (data.mediaType) query.mediaType = data.mediaType
        super.setRequestParam(query)
    }
}

// 7. Get full information of a Media (with role-based access)
class ProductMediaGetFullInfoRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }
    setParams(data) {
        // data = { mediaId }
        super.setTag(`v1/product-media/${data.mediaId}/full-info`)
        super.setRequestParam(data)
    }
}

// ============================================================
//  Playlist Endpoints
// ============================================================

// 8. Create a new playlist (admin only)
class ProductMediaCreatePlaylistRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
        super.setTag('v1/product-media/playlist')
    }
    setParams(data) {
        // data = { productId, title, description, sortOrder }
        super.setRequestParamDataObj(data)
    }
}

// 9. Update a playlist (admin only)
class ProductMediaUpdatePlaylistRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('PUT')
    }
    setParams(data) {
        // data = { playlistId, title, description, isActive, sortOrder }
        super.setTag(`v1/product-media/playlist/${data.playlistId}`)
        super.setRequestParamDataObj(data)
    }
}

// 10. Delete a playlist (admin only)
class ProductMediaDeletePlaylistRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('DELETE')
    }
    setParams(data) {
        // data = { playlistId }
        super.setTag(`v1/product-media/playlist/${data.playlistId}`)
        super.setRequestParam(data)
    }
}

// 11. Get playlists of a product (with role-based access)
class ProductMediaGetPlaylistsByProductRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }
    setParams(data) {
        // data = { productId }
        super.setTag(`v1/product-media/playlist/product/${data.productId}`)
        super.setRequestParam(data)
    }
}

// 12. Add a Media to a playlist (admin only)
class ProductMediaAddMediaToPlaylistRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }
    setParams(data) {
        // data = { playlistId, mediaId, sortOrder }
        super.setTag(`v1/product-media/playlist/${data.playlistId}/add-media`)
        super.setRequestParamDataObj(data)
    }
}

// 13. Remove a Media from a playlist (admin only)
class ProductMediaRemoveMediaFromPlaylistRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('DELETE')
    }
    setParams(data) {
        // data = { playlistId, mediaId }
        super.setTag(`v1/product-media/playlist/${data.playlistId}/remove-media/${data.mediaId}`)
        super.setRequestParam(data)
    }
}

// 14. Reorder items in a playlist (admin only)
class ProductMediaReorderPlaylistRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('PUT')
    }
    setParams(data) {
        // data = { playlistId, orderMap: { fileEntryId: newOrder, ... } }
        super.setTag(`v1/product-media/playlist/${data.playlistId}/reorder`)
        // The request body is an object with key-value pairs: fileEntryId -> new sortOrder
        super.setRequestParamDataObj(data.orderMap)
    }
}

// 15. Get items of a playlist (with role-based access)
class ProductMediaGetPlaylistItemsRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }
    setParams(data) {
        // data = { playlistId }
        super.setTag(`v1/product-media/playlist/${data.playlistId}/items`)
        super.setRequestParam(data)
    }
}

// ============================================================
//  Additional endpoints (admin / attach/detach)
// ============================================================

// 16. Get all files with product assignment info (admin only)
class ProductMediaGetAllFilesRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
        super.setTag('v1/product-media/files')
    }
    setParams(data) {
        // data = { mediaType?, keyword? }
        super.setRequestParam(data)
    }
}

// 17. Attach an existing file to a product (admin only)
class ProductMediaAttachFileRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
        super.setTag('v1/product-media/attach')
    }
    setParams(data) {
        // data = { productId, fileEntryId, priority?, isMain?, isReachable?, isLocked? }
        super.setRequestParamDataObj(data)
    }
}

// 18. Detach a Media from a product (without deleting the file) (admin only)
class ProductMediaDetachFileRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('DELETE')
    }
    setParams(data) {
        // data = { mediaId }
        super.setTag(`v1/product-media/detach/${data.mediaId}`)
        super.setRequestParam(data)
    }
}

class ProductMediaPlaylistsRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('GET')
    }

    setParams(productId) {
        super.setTag(`v1/product-media/playlist/product/${productId}`)
    }
}

class ProductMediaPlaylistsAttachRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }

    setParams(data) {
        super.setTag(`v1/product-media/attach-playlist/${data.playlistId}/to-product/${data.productId}`)
    }
}

class ProductMediaPlaylistsDetachRequest extends WebServiceRequest {
    constructor(context) {
        super(context)
        super.setMethod('POST')
    }

    setParams(data) {
        super.setTag(`v1/product-media/detach-playlist/${data.playlistId}`)
    }
}




// ============================================================
//  Exports
// ============================================================

export {
    ProductMediaUploadRequest,
    ProductMediaDeleteRequest,
    ProductMediaUpdateRequest,
    ProductMediaToggleLockRequest,
    ProductMediaChangePriorityRequest,
    ProductMediaPlaylistsRequest,
    ProductMediaPlaylistsDetachRequest,
    ProductMediaPlaylistsAttachRequest,
    ProductMediaGetByProductRequest,
    ProductMediaGetFullInfoRequest,
    ProductMediaCreatePlaylistRequest,
    ProductMediaUpdatePlaylistRequest,
    ProductMediaDeletePlaylistRequest,
    ProductMediaGetPlaylistsByProductRequest,
    ProductMediaAddMediaToPlaylistRequest,
    ProductMediaRemoveMediaFromPlaylistRequest,
    ProductMediaReorderPlaylistRequest,
    ProductMediaGetPlaylistItemsRequest,
    ProductMediaGetAllFilesRequest,
    ProductMediaAttachFileRequest,
    ProductMediaDetachFileRequest
}
